<template>
    <tailwind-layout>
        <Head title="User Lookup" />

        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">User Lookup</h1>
                <p class="mt-1 text-sm text-gray-600">
                    Search for a user to open their support console — status, applications,
                    verification, and unblock actions. Impersonation lives on each user's page.
                </p>
            </header>

            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-yellow-700">
                            <strong>Security Notice:</strong> You are logged in as
                            <strong>{{ currentUser.name }} ({{ currentUser.email }})</strong>.
                            Viewing a user and any unblock actions are logged and audited.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Search -->
            <div class="max-w-md">
                <label for="search" class="sr-only">Search users</label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <input
                        v-model="search"
                        @input="searchUsers"
                        type="text"
                        id="search"
                        class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm"
                        placeholder="Search by name, email, or ID..."
                    />
                </div>
            </div>

            <!-- Results -->
            <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <!-- Desktop table -->
                <table class="hidden md:table min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ID</th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                            <th scope="col" class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr
                            v-for="user in users.data"
                            :key="user.id"
                            class="group hover:bg-gray-50 cursor-pointer"
                            @click="viewSupport(user)"
                        >
                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ user.id }}</td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="text-sm font-medium text-gray-900">{{ user.name }}</div>
                                <div class="text-sm text-gray-500">{{ user.email }}</div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize" :class="roleBadgeClass(user.role)">{{ user.role }}</span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span v-if="user.deleted_at" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Deleted</span>
                                <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right">
                                <font-awesome-icon :icon="faChevronRight" class="h-4 w-4 text-gray-300 group-hover:text-rent-risk-blue-400 transition" aria-label="Open support console" />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Mobile card-stack -->
                <ul class="md:hidden divide-y divide-gray-200">
                    <li
                        v-for="user in users.data"
                        :key="user.id"
                        class="flex items-center justify-between gap-3 p-4 cursor-pointer active:bg-gray-50"
                        @click="viewSupport(user)"
                    >
                        <div class="min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="truncate text-sm font-medium text-gray-900">{{ user.name }}</span>
                                <span class="shrink-0 px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize" :class="roleBadgeClass(user.role)">{{ user.role }}</span>
                            </div>
                            <div class="truncate text-sm text-gray-500">{{ user.email }}</div>
                            <div class="mt-1 flex items-center gap-2 text-xs">
                                <span class="text-gray-400">#{{ user.id }}</span>
                                <span v-if="user.deleted_at" class="px-2 inline-flex font-semibold rounded-full bg-red-100 text-red-800">Deleted</span>
                                <span v-else class="px-2 inline-flex font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                            </div>
                        </div>
                        <font-awesome-icon :icon="faChevronRight" class="h-4 w-4 shrink-0 text-gray-300" aria-label="Open support console" />
                    </li>
                </ul>

                <!-- Empty State -->
                <div v-if="users.data.length === 0" class="text-center py-12">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                    <p class="mt-1 text-sm text-gray-500">Try adjusting your search criteria.</p>
                </div>

                <!-- Pagination -->
                <div v-if="users.data.length > 0" class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                    <pagination :links="users.links" :results="users" :type="'users'" />
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import Pagination from '@/Shared/Pagination.vue';
import { Head, router } from '@inertiajs/vue3';
import { ref } from 'vue';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import BridgeBackLink from '@/Shared/BridgeBackLink.vue';

const roleBadgeClass = (role) => {
    if (role === 'agent') return 'bg-violet-100 text-violet-700';
    if (role === 'applicant') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
};

export default {
    components: {
        TailwindLayout,
        Pagination,
        Head,
        FontAwesomeIcon,
        BridgeBackLink,
    },

    props: {
        users: Object,
        filters: Object,
        isImpersonating: Boolean,
        currentUser: Object,
    },

    setup(props) {
        const search = ref(props.filters.search || '');

        const searchUsers = debounce(() => {
            router.get(route('impersonation.index'), { search: search.value }, {
                preserveState: true,
                replace: true,
            });
        }, 300);

        const viewSupport = (user) => {
            router.visit(route('bridge.support.show', user.id));
        };

        return {
            search,
            searchUsers,
            viewSupport,
            roleBadgeClass,
            faChevronRight,
        };
    },
};
</script>
